import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Currency input formatter - automatically moves decimal as you type
// e.g., typing "45000" shows "450.00"
const formatCurrencyInput = (value) => {
  // Remove all non-numeric characters
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (!numericValue) return '0.00';
  
  // Convert to cents then to dollars
  const cents = parseInt(numericValue, 10);
  const dollars = (cents / 100).toFixed(2);
  
  return dollars;
};

// Parse formatted currency back to number
const parseCurrencyValue = (formattedValue) => {
  return parseFloat(formattedValue) || 0;
};

const MyRatesSimplified = () => {
  const [user, setUser] = useState(null);
  const [rates, setRates] = useState({
    material_type: 'asphalt',
    material_cost_per_sqft: 1.50,
    material_pricing_unit: 'sqft', // 'sqft' or 'square' (100 sqft)
    labor_per_sqft: 2.0,
    labor_pricing_unit: 'sqft', // 'sqft' or 'square' (100 sqft)
    steep_roof_threshold_degrees: 30,
    steep_roof_charge_type: 'percent',
    steep_roof_charge_amount: 15.0,
    overhead_profit_percent: 20.0,
    sales_tax_rate: 0.0
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Raw input values for currency formatting (stores the cents value as string)
  const [materialCostRaw, setMaterialCostRaw] = useState('');
  const [laborCostRaw, setLaborCostRaw] = useState('');

  // Handle currency input change with auto-decimal formatting
  const handleCurrencyChange = (field, rawValue, setRawValue) => {
    // Remove non-numeric characters
    const numericOnly = rawValue.replace(/[^\d]/g, '');
    setRawValue(numericOnly);
    
    // Convert to actual dollar value
    const dollarValue = parseInt(numericOnly || '0', 10) / 100;
    handleChange(field, dollarValue);
  };

  // Load user data and rates on mount - single useEffect
  useEffect(() => {
    const initializeComponent = async () => {
      console.log('=== INITIALIZING MY RATES COMPONENT ===');
      
      // Get user from localStorage - CORRECT KEY IS 'rooftrack_user'
      const userData = JSON.parse(localStorage.getItem('rooftrack_user') || '{}');
      console.log('User data from localStorage:', userData);
      setUser(userData);

      // If user exists, load their rates immediately
      if (userData && userData.id) {
        console.log('User ID found:', userData.id);
        await loadRatesFromBackend();
      } else {
        console.log('No user ID found, using default rates');
        setIsLoading(false);
      }
    };

    initializeComponent();
  }, []); // Only run once on mount

  const loadRatesFromBackend = async () => {
    console.log('=== LOADING RATES FROM BACKEND ===');
    const token = localStorage.getItem('rooftrack_token');
    console.log('Token exists:', !!token);
    
    if (!token) {
      console.error('No authentication token found');
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/rates/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Rates loaded successfully:', response.data);
      
      if (response.data && response.data.rates) {
        const savedRates = response.data.rates;
        console.log('Setting rates state to:', savedRates);
        setRates(savedRates);
        
        // Initialize raw currency values for per-square inputs
        // Convert dollar amount to cents string for the raw input
        const materialCents = Math.round((savedRates.material_cost_per_sqft || 1.50) * 100);
        const laborCents = Math.round((savedRates.labor_per_sqft || 2.0) * 100);
        setMaterialCostRaw(materialCents.toString());
        setLaborCostRaw(laborCents.toString());
        
        setMessage({ type: 'success', text: '✓ Loaded your saved rates' });
        setTimeout(() => setMessage(null), 2000);
      }
    } catch (error) {
      console.error('❌ Error loading rates:', error.response?.status, error.message);
      
      if (error.response?.status === 404) {
        console.log('No saved rates found, keeping defaults');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        setMessage({ type: 'error', text: 'Authentication error. Please log in again.' });
      } else {
        console.error('Unexpected error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    // String fields that should NOT be parsed as numbers
    const stringFields = [
      'material_type', 'material_pricing_unit', 'labor_pricing_unit',
      'steep_roof_charge_type', 'per_story_charge_type'
    ];
    
    const parsedValue = stringFields.includes(field) ? value : (parseFloat(value) || 0);
    
    console.log(`Changing ${field} from ${rates[field]} to ${parsedValue} (type: ${typeof parsedValue})`);
    setRates(prev => ({ ...prev, [field]: parsedValue }));
  };

  const handleSave = async () => {
    console.log('=== SAVING RATES ===');
    console.log('Rates to save:', rates);
    
    const token = localStorage.getItem('rooftrack_token');
    if (!token) {
      setMessage({ type: 'error', text: 'Not authenticated. Please log in.' });
      return;
    }
    
    try {
      setIsSaving(true);
      const response = await axios.post(`${BACKEND_URL}/api/rates/me`, { rates }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Save successful:', response.data);
      setMessage({ type: 'success', text: '✓ Rates saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
      
      // Immediately verify by reloading from backend
      console.log('Verifying save by reloading...');
      await loadRatesFromBackend();
      
    } catch (error) {
      console.error('❌ Save failed:', error.response?.status, error.message);
      setMessage({ type: 'error', text: 'Failed to save rates. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all rates to defaults?')) {
      setRates({
        material_type: 'asphalt',
        material_cost_per_sqft: 1.50,
        material_pricing_unit: 'sqft',
        labor_per_sqft: 2.0,
        labor_pricing_unit: 'sqft',
        steep_roof_threshold_degrees: 30,
        steep_roof_charge_type: 'percent',
        steep_roof_charge_amount: 15.0,
        overhead_profit_percent: 20.0,
        sales_tax_rate: 0.0
      });
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <DollarSign className="w-8 h-8 mr-3 text-blue-600" />
              My Rates
            </h1>
            <p className="text-gray-600 mt-2">Configure your pricing rates and calculations</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleReset} variant="outline">
              Reset to Defaults
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700">
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'
          }`}>
            {message.text}
          </div>
        )}

        {/* Material Costs by Roof Type */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-semibold text-gray-900">Material Costs by Roof Type</h2>
            <div className="flex items-center gap-2">
              <Label htmlFor="material_pricing_unit" className="text-sm text-gray-500">Unit:</Label>
              <select
                id="material_pricing_unit"
                value={rates.material_pricing_unit || 'sqft'}
                onChange={(e) => handleChange('material_pricing_unit', e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
                data-testid="material-pricing-unit"
              >
                <option value="sqft">Per Sq Ft</option>
                <option value="square">Per Square (100 sq ft)</option>
              </select>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Set different material rates for each shingle type. Rates are {rates.material_pricing_unit === 'square' ? 'per square (100 sq ft)' : 'per square foot'}.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="landmark_mat">Landmark (Baseline Architectural)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="landmark_mat"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates.landmark_material_per_sqft ?? rates.material_cost_per_sqft ?? 1.50}
                  onChange={(e) => handleChange('landmark_material_per_sqft', e.target.value)}
                  className="pl-7"
                  data-testid="landmark-material-rate"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="landmark_pro_mat">Landmark PRO</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="landmark_pro_mat"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates.landmark_pro_material_per_sqft ?? rates.material_cost_per_sqft ?? 2.00}
                  onChange={(e) => handleChange('landmark_pro_material_per_sqft', e.target.value)}
                  className="pl-7"
                  data-testid="landmark-pro-material-rate"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="standing_seam_mat">Standing Seam Metal</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="standing_seam_mat"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates.standing_seam_material_per_sqft ?? rates.material_cost_per_sqft ?? 4.50}
                  onChange={(e) => handleChange('standing_seam_material_per_sqft', e.target.value)}
                  className="pl-7"
                  data-testid="standing-seam-material-rate"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="luxury_mat">Luxury Line</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="luxury_mat"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates.luxury_material_per_sqft ?? rates.material_cost_per_sqft ?? 6.00}
                  onChange={(e) => handleChange('luxury_material_per_sqft', e.target.value)}
                  className="pl-7"
                  data-testid="luxury-material-rate"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Labor Costs by Roof Type */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-semibold text-gray-900">Labor Costs by Roof Type</h2>
            <div className="flex items-center gap-2">
              <Label htmlFor="labor_pricing_unit" className="text-sm text-gray-500">Unit:</Label>
              <select
                id="labor_pricing_unit"
                value={rates.labor_pricing_unit || 'sqft'}
                onChange={(e) => handleChange('labor_pricing_unit', e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
                data-testid="labor-pricing-unit"
              >
                <option value="sqft">Per Sq Ft</option>
                <option value="square">Per Square (100 sq ft)</option>
              </select>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Set different labor rates for each shingle type. Rates are {rates.labor_pricing_unit === 'square' ? 'per square (100 sq ft)' : 'per square foot'}.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="landmark_lab">Landmark (Baseline Architectural)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="landmark_lab"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates.landmark_labor_per_sqft ?? rates.labor_per_sqft ?? 2.00}
                  onChange={(e) => handleChange('landmark_labor_per_sqft', e.target.value)}
                  className="pl-7"
                  data-testid="landmark-labor-rate"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="landmark_pro_lab">Landmark PRO</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="landmark_pro_lab"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates.landmark_pro_labor_per_sqft ?? rates.labor_per_sqft ?? 2.25}
                  onChange={(e) => handleChange('landmark_pro_labor_per_sqft', e.target.value)}
                  className="pl-7"
                  data-testid="landmark-pro-labor-rate"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="standing_seam_lab">Standing Seam Metal</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="standing_seam_lab"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates.standing_seam_labor_per_sqft ?? rates.labor_per_sqft ?? 3.50}
                  onChange={(e) => handleChange('standing_seam_labor_per_sqft', e.target.value)}
                  className="pl-7"
                  data-testid="standing-seam-labor-rate"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="luxury_lab">Luxury Line</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="luxury_lab"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rates.luxury_labor_per_sqft ?? rates.labor_per_sqft ?? 4.00}
                  onChange={(e) => handleChange('luxury_labor_per_sqft', e.target.value)}
                  className="pl-7"
                  data-testid="luxury-labor-rate"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Automatic Steep Roof Charge */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Automatic Steep Roof Charge</h2>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <p className="text-sm text-blue-900">
              <strong>How it works:</strong> When creating an estimate, if the detected roof pitch is <strong>≥ your threshold</strong>, the system will automatically add the charge you configure below. Your overhead/profit percentage will be applied to this charge.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="steep_threshold">Steep Roof Threshold (degrees)</Label>
              <Input
                id="steep_threshold"
                type="number"
                min="20"
                max="60"
                value={rates.steep_roof_threshold_degrees}
                onChange={(e) => handleChange('steep_roof_threshold_degrees', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Typical: 30° (7:12 pitch)</p>
            </div>
            
            <div>
              <Label htmlFor="steep_charge_type">Charge Type</Label>
              <select
                id="steep_charge_type"
                value={rates.steep_roof_charge_type}
                onChange={(e) => handleChange('steep_roof_charge_type', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                key={`steep-type-${rates.steep_roof_charge_type}`}
              >
                <option value="percent">Percentage (%)</option>
                <option value="dollar">Dollar Amount ($)</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="steep_charge_amount">
                {rates.steep_roof_charge_type === 'percent' 
                  ? 'Percentage Amount (%)' 
                  : 'Dollar Amount ($)'}
              </Label>
              <div className="relative">
                <Input
                  id="steep_charge_amount"
                  type="number"
                  step="0.1"
                  min="0"
                  value={rates.steep_roof_charge_amount}
                  onChange={(e) => handleChange('steep_roof_charge_amount', e.target.value)}
                  className="pr-8"
                  placeholder={rates.steep_roof_charge_type === 'percent' ? 'e.g., 15' : 'e.g., 1500'}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  {rates.steep_roof_charge_type === 'percent' ? '%' : '$'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {rates.steep_roof_charge_type === 'percent' 
                  ? 'Enter 15 for 15% charge (typical: 10-20%)' 
                  : 'Enter 1500 for $1,500 charge (typical: $500-$2,000)'}
              </p>
            </div>
          </div>
        </div>

        {/* Overhead & Profit */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Overhead & Profit</h2>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
            <p className="text-sm text-amber-900">
              <strong>Applied to each line item:</strong> This percentage will be added to materials, labor, and steep roof charges individually. The total shown on estimates will equal the sum of all marked-up line items.
            </p>
          </div>
          <div className="max-w-md">
            <Label htmlFor="overhead_profit">Overhead & Profit Percentage</Label>
            <div className="relative">
              <Input
                id="overhead_profit"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={rates.overhead_profit_percent}
                onChange={(e) => handleChange('overhead_profit_percent', e.target.value)}
                className="pr-8"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Applied to each cost component (typically 20%)
            </p>
          </div>
        </div>

        {/* State Sales Tax */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">State Sales Tax</h2>
          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-4">
            <p className="text-sm text-purple-900">
              <strong>Note:</strong> Sales tax laws vary by state. Some states exempt labor, others tax the full contract. Consult your state's tax authority for specifics. Tax is applied to the subtotal after all other charges.
            </p>
          </div>
          <div className="max-w-md">
            <Label htmlFor="sales_tax">Sales Tax Rate</Label>
            <div className="relative">
              <Input
                id="sales_tax"
                type="number"
                step="0.125"
                min="0"
                max="15"
                value={rates.sales_tax_rate || 0}
                onChange={(e) => handleChange('sales_tax_rate', e.target.value)}
                className="pr-8"
                placeholder="0"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Enter your state's sales tax rate (e.g., 7.25 for 7.25%). Set to 0 if not applicable.
            </p>
            <div className="mt-3 text-sm text-gray-500 bg-gray-50 p-3 rounded">
              <strong>Common rates:</strong><br/>
              NC: 6.75-7.5% | TX: 6.25-8.25% | FL: 6-7.5% | CA: 7.25-10.25%
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MyRatesSimplified;
